import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, message, Space, Spin, Table, Tag, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
  FlagOutlined,
  MailOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useSelector } from "react-redux";
import { serverFetch } from "../../server";
import { dictsChannelVidName } from "../../extensions/dicts";
import Dialog from "../dialogs/dialog";
import "../dialogs/dialogs.css";

dayjs.extend(isoWeek);

const MESSAGE_DIRECTION_INCOMING = 1;
const MESSAGE_DIRECTION_OUTCOMING = -1;

const RU_ISO_WEEKDAY_SHORT = {
  1: "пн",
  2: "вт",
  3: "ср",
  4: "чт",
  5: "пт",
  6: "сб",
  7: "вс",
};

const formatRelativeLastMessageTime = (value) => {
  if (value == null || value === "") return "";
  const m = dayjs(value);
  if (!m.isValid()) return "";
  const now = dayjs();
  if (m.isSame(now, "day")) return m.format("HH:mm");
  if (m.isSame(now, "isoWeek")) {
    const dow = RU_ISO_WEEKDAY_SHORT[m.isoWeekday()] ?? "";
    return dow ? `${dow}. ${m.format("HH:mm")}` : m.format("HH:mm");
  }
  return m.format("DD.MM.YYYY HH:mm");
};

const directionMeta = (dir) => {
  const n = Number(dir);
  if (n === MESSAGE_DIRECTION_INCOMING)
    return {
      icon: <ArrowLeftOutlined aria-hidden />,
      title: "Входящее",
    };
  if (n === MESSAGE_DIRECTION_OUTCOMING)
    return {
      icon: <ArrowRightOutlined aria-hidden />,
      title: "Исходящее",
    };
  return { icon: null, title: "" };
};

const tagHexColor = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

const uniqueTagsById = (tags) =>
  [...new Map((Array.isArray(tags) ? tags : []).map((t) => [t.id, t])).values()];

const IndicatorIcon = ({ icon: Icon, className, color }) => (
  <Icon className={className} style={color ? { color } : undefined} />
);

const isDialogWithoutManager = (d) => {
  const v = d?.userId;
  if (v == null || v === "") return true;
  const n = Number(v);
  return Number.isFinite(n) && n <= 0;
};

const isDialogWithoutClient = (d) => {
  const v = d?.clientId;
  if (v == null || v === "") return true;
  const n = Number(v);
  return Number.isFinite(n) && n <= 0;
};

const isDialogUnread = (d) => {
  const readId = d?.lastMessageIdIsRead;
  const lastId = d?.lastMessageId;
  if (readId == null) return true;
  if (lastId == null) return false;
  return Number(readId) < Number(lastId);
};

const ClientDialogs = ({ id, exposeOnRefresh }) => {
  const [form] = Form.useForm();
  const [dialogsList, setDialogsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDialogId, setModalDialogId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const user = useSelector((s) => s.user);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const getDialogsData = useCallback(() => {
    if (!id || !user?.companyId) return;
    setLoading(true);
    serverFetch("Message/Dialog", {
      queryParams: {
        companyId: user.companyId,
        clientId: id,
        deleted: false,
        begPer: dayjs().subtract(5, "year").startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endPer: dayjs().add(1, "day").endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
    })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setDialogsList(
          [...list].sort((a, b) => dayjs(b.lastUpdated).valueOf() - dayjs(a.lastUpdated).valueOf())
        );
        setSelectedRowKeys([]);
      })
      .catch((e) => message.error(e.userMessage || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [id, user?.companyId]);

  const getMessages = useCallback(
    (dialogId) => {
      if (!dialogId) return;
      setMessagesLoading(true);
      serverFetch("Message/Message", { queryParams: { dialogId } })
        .then((data) => {
          setMessages(Array.isArray(data) ? data : []);
          setTimeout(scrollToBottom, 50);
        })
        .catch((e) => message.error(e.userMessage || "Ошибка загрузки"))
        .finally(() => setMessagesLoading(false));
    },
    [scrollToBottom]
  );

  useEffect(() => {
    getDialogsData();
  }, [getDialogsData]);

  useEffect(() => {
    if (exposeOnRefresh) {
      exposeOnRefresh(() => getDialogsData);
    }
  }, [exposeOnRefresh, getDialogsData]);

  useEffect(() => {
    if (!modalDialogId) {
      setMessages([]);
      return;
    }
    getMessages(modalDialogId);
  }, [modalDialogId, getMessages]);

  const openDialogModal = (dialog) => {
    setSelectedRowKeys([dialog.id]);
    setModalDialogId(dialog.id);
  };

  const handleDialogClose = useCallback(() => {
    setModalDialogId(null);
    setSelectedRowKeys([]);
    setMessages([]);
    getDialogsData();
  }, [getDialogsData]);

  const columns = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 72,
        render: (dialogId) => (dialogId != null ? dialogId : "—"),
      },
      {
        title: "Индикаторы",
        key: "flags",
        width: 220,
        render: (_, d) => {
          const tagsList = uniqueTagsById(d.tags);
          return (
            <Space wrap size={[4, 6]} className="dialogs-indicators-cell">
              {isDialogUnread(d) && (
                <Tooltip title="Непрочитано">
                  <span>
                    <MailOutlined className="dialogs-icon-unread" style={{ color: "#FFBB3B" }} />
                  </span>
                </Tooltip>
              )}
              {isDialogWithoutManager(d) && (
                <Tooltip title="Лид без менеджера">
                  <span>
                    <IndicatorIcon icon={ClockCircleOutlined} className="dialogs-icon-awaiting" color="#FFBB3B" />
                  </span>
                </Tooltip>
              )}
              {isDialogWithoutClient(d) && (
                <Tooltip title="Лид без клиента">
                  <span>
                    <IndicatorIcon icon={TeamOutlined} className="dialogs-icon-lead" color="#FF4040" />
                  </span>
                </Tooltip>
              )}
              {(d.countDialog ?? 0) > 1 && (
                <Tooltip title="Несколько диалогов у клиента">
                  <span>
                    <IndicatorIcon icon={FlagOutlined} className="dialogs-icon-multi" color="#32CD32" />
                  </span>
                </Tooltip>
              )}
              {tagsList.map((t) => {
                const hex = tagHexColor(t.color);
                const label = t.value ?? "";
                return (
                  <Tooltip key={`${t.id}-${t.objectVid ?? ""}`} title={label || "Тег"}>
                    <span>
                      <Tag
                        className="dialogs-indicator-tag"
                        style={{
                          marginInlineEnd: 0,
                          ...(hex
                            ? {
                                backgroundColor: hex,
                                borderColor: hex,
                                color: "#fff",
                              }
                            : {}),
                        }}
                      >
                        {label}
                      </Tag>
                    </span>
                  </Tooltip>
                );
              })}
            </Space>
          );
        },
      },
      {
        title: "Канал",
        key: "source",
        width: 120,
        ellipsis: true,
        render: (_, d) => d.channelName || dictsChannelVidName(d.vid) || "",
      },
      {
        title: "Сообщение",
        key: "message",
        width: 300,
        ellipsis: true,
        render: (_, d) => {
          const ts = d.lastMessageCreatedOn ?? d.lastUpdated;
          const timeStr = formatRelativeLastMessageTime(ts);
          const { icon, title: dirTitle } = directionMeta(d.lastMessageDirection);
          const text = (d.lastMessageText && String(d.lastMessageText).trim()) || "";
          return (
            <div className="dialogs-msg-preview">
              <div className="dialogs-msg-preview-text" title={text !== "" ? text : undefined}>
                {text}
              </div>
              <div className="dialogs-msg-preview-meta">
                {icon ? (
                  <Tooltip title={dirTitle}>
                    <span className="dialogs-msg-dir-icon">{icon}</span>
                  </Tooltip>
                ) : (
                  <span className="dialogs-msg-dir-placeholder" />
                )}
                <span className="dialogs-msg-preview-time">{timeStr || ""}</span>
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  const rowSelection = {
    type: "radio",
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      const dialogId = keys[0];
      if (dialogId != null) setModalDialogId(dialogId);
    },
    renderCell: (checked) => <div className={checked ? "list-indicator" : ""} />,
    columnWidth: 40,
  };

  return (
    <>
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 24 }} colon={false} disabled={loading}>
        <Spin spinning={loading}>
          <Table
            dataSource={dialogsList}
            columns={columns}
            pagination={false}
            size="small"
            className="table-item dialogs-table"
            rowKey="id"
            rowClassName={(_, index) => (index % 2 === 0 ? "row-even" : "row-odd")}
            rowSelection={rowSelection}
            tableLayout="fixed"
            scroll={{ x: 752 }}
            onRow={(record) => ({
              onClick: (e) => {
                if (
                  e.target.closest?.(
                    "button,a,.ant-checkbox-wrapper,.ant-checkbox,.ant-radio-wrapper,.ant-radio"
                  )
                )
                  return;
                openDialogModal(record);
              },
            })}
          />
        </Spin>
      </Form>

      {modalDialogId != null && (
        <Dialog
          id={modalDialogId}
          onClose={handleDialogClose}
          messages={messages}
          setMessages={setMessages}
          messagesLoading={messagesLoading}
          messagesEndRef={messagesEndRef}
          scrollToBottom={scrollToBottom}
          onSent={getDialogsData}
        />
      )}
    </>
  );
};

export default ClientDialogs;
